import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useNavigate } from 'react-router-dom'

import { getCharsCountForService } from '@buffer-mono/char-toolkit'
import { Text } from '@buffer-mono/popcorn'

import { getGuestNetworkIdForService } from '~publish/helpers/guestNetworks'
import { PUBLIC_PUBLISH_ROUTE_PREFIX } from '~publish/helpers/publicPublishPaths'
import { newPost } from '~publish/legacy/routes'

import { CommentHoverActionsView } from '../CommentDetails/CommentHoverActions'
import { copyCommentUrlToClipboard } from '../CommentDetails/copyCommentLink'
import {
  AnimatedPostMiniatureFrame,
  CommentCardView,
  CommentThreadFrame,
  getCommentCardAriaLabel,
} from '../Comments/CommentCard'
import { CommentActionsView } from '../Comments/CommentCard/CommentActions'
import { ReplyingToLabel } from '../Comments/CommentsList/CommentReplyFormHeader'
import { TextContent } from '../Comments/CommentCard/CommentText'
import { formatMentionUsername } from '../Comments/CommentReplyForm/utils/replyFormUtils'
import { ReactionContainer, ReactionIcon } from '../components/APIReaction'
import { EngagementAvatar } from '../components/EngagementAvatar'
import { EngagementHeader } from '../components/EngagementHeader'
import { EngagementItem } from '../components/EngagementItem'
import { EngagementReplyFormView } from '../components/EngagementReplyForm'
import {
  EmojiPickerButton,
  StartPostButton,
  SubmitReplyShortcutHint,
  useReplyEmojiInsert,
} from '../components/EngagementReplyFormFooter'
import { EngagementReplySuggestions } from '../components/EngagementReplySuggestions'
import { ReplyForm } from '../components/ReplyForm'
import { SavedRepliesEmptyPopover } from '../components/SavedRepliesPopover'
import { ExpandableEngagementText } from '../components/ExpandableEngagementText'
import { useEngagementInteraction } from '../hooks/engagement/useEngagementInteraction'
import { PostMiniatureView } from '../PostMiniature'

import {
  EXAMPLE_REPLY_ACCOUNT_NAME,
  getExampleSourcePost,
} from './exampleComments'
import type { ExampleComment } from './exampleComments'
import type { ExampleCommentState, ExampleReply } from './exampleReplay'
import { logExampleAction } from './logExampleAction'

type PublicExampleCommentCardProps = {
  comment: ExampleComment
  state: ExampleCommentState
  expanded: boolean
  onManualExpansion: (commentId: string | null) => void
  onExpandNext: (fromCommentId?: string, onlyRepliable?: boolean) => void
  onExpandPrevious: () => void
  onReply: (text: string) => void
  onToggleLike: () => void
  onResolve: () => void
  onReopen: () => void
}

export function PublicExampleCommentCard({
  comment,
  state,
  expanded,
  onManualExpansion,
  onExpandNext,
  onExpandPrevious,
  onReply,
  onToggleLike,
  onResolve,
  onReopen,
}: PublicExampleCommentCardProps): JSX.Element {
  const cardRef = useRef<HTMLLIElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [publishedAt] = useState(() =>
    new Date(Date.now() - comment.minutesAgo * 60_000).toISOString(),
  )
  const [sourcePost] = useState(() => getExampleSourcePost(comment, Date.now()))
  const interaction = useEngagementInteraction({
    id: comment.id,
    expanded,
    onManualExpansion,
    onExpandNext,
    onExpandPrevious,
    onHoverReset: () => setIsHovered(false),
    reactionSelector: '[data-comment-reaction], [data-comment-actions]',
  })
  const headerId = `example-comment-header-${comment.id}`
  const isResolved = state.status !== 'unreplied'
  const isDismissed = state.status === 'dismissed'
  const showResolvedStyle = isResolved && !expanded
  const hasReplies = state.replies.length > 0

  useEffect(() => {
    if (!expanded || !isResolved) return
    cardRef.current?.querySelector<HTMLElement>('[role="button"]')?.focus()
  }, [expanded, isResolved])

  return (
    <CommentCardView
      ref={cardRef}
      commentId={comment.id}
      expanded={expanded}
      resolved={showResolvedStyle}
      dimmed={showResolvedStyle && !isHovered}
      ariaLabel={getCommentCardAriaLabel(comment.authorName, comment.text)}
      onClick={interaction.handleClick}
      onKeyDown={interaction.handleKeyDown}
      onMouseEnter={(): void => setIsHovered(true)}
      onMouseLeave={(): void => setIsHovered(false)}
      miniature={
        expanded &&
        !isResolved && (
          <AnimatedPostMiniatureFrame>
            <PostMiniatureView
              post={sourcePost}
              showChannel
              mediaFirst={false}
            />
          </AnimatedPostMiniatureFrame>
        )
      }
      actions={
        <CommentActionsView
          mode={
            isDismissed
              ? 'reopen'
              : state.status === 'unreplied'
                ? 'resolve'
                : null
          }
          visible={isDismissed || isHovered || expanded}
          viewOnly={false}
          disabled={false}
          viewOnlyTooltip={null}
          expanded={expanded}
          isInterimExpanded={isResolved && expanded}
          onResolve={(): void => {
            logExampleAction('resolve', comment.id)
            onResolve()
            if (expanded) {
              interaction.handleCollapse()
              onExpandNext(comment.id, true)
            }
          }}
          onReopen={(): void => {
            logExampleAction('reopen', comment.id)
            onReopen()
          }}
        />
      }
    >
      <CommentThreadFrame
        commentId={comment.id}
        status={state.status}
        onlyComment
        expanded={expanded}
        aria-label={`Comment thread from ${comment.authorName} - ${
          state.status === 'replied'
            ? 'replied to'
            : isDismissed
              ? 'dismissed'
              : 'needs attention'
        }`}
      >
        <EngagementItem
          variant="root"
          aria-labelledby={headerId}
          avatar={
            <EngagementAvatar
              avatar=""
              username={comment.authorName}
              channel={expanded ? undefined : { service: comment.service }}
              showConnector={hasReplies || (expanded && !isResolved)}
            />
          }
        >
          <EngagementHeader
            id={headerId}
            username={
              <Text weight="bold" translate="no">
                {comment.authorName}
              </Text>
            }
            timestamp={publishedAt}
            trailing={
              <CommentHoverActionsView
                externalUrl={null}
                onCopyLink={(): void => {
                  logExampleAction('copy link', comment.id)
                  void copyCommentUrlToClipboard(
                    `${window.location.origin}${PUBLIC_PUBLISH_ROUTE_PREFIX}/community?preview=true#${comment.id}`,
                  )
                }}
              />
            }
          />
          <ExpandableEngagementText>
            <TextContent text={comment.text} serviceType={comment.service} />
          </ExpandableEngagementText>
          {comment.likingIcon && (
            <ReactionContainer>
              <ReactionIcon
                likingIcon={comment.likingIcon}
                isLiked={state.liked}
                networkName={comment.networkName}
                targetLabel={`comment from ${comment.authorName}: ${comment.text}`}
                onClick={(event): void => {
                  event.stopPropagation()
                  logExampleAction(state.liked ? 'unlike' : 'like', comment.id)
                  onToggleLike()
                }}
              />
            </ReactionContainer>
          )}
        </EngagementItem>
        {state.replies.map((reply, index) => (
          <ExampleReplyRow
            key={reply.id}
            reply={reply}
            showConnector={
              index < state.replies.length - 1 || (expanded && !isResolved)
            }
          />
        ))}
        {expanded && !isResolved && (
          <ExampleReplyForm
            comment={comment}
            onSubmit={(text): void => {
              logExampleAction('reply', comment.id, text)
              onReply(text)
              interaction.handleCollapse()
              onExpandNext(comment.id, true)
            }}
          />
        )}
      </CommentThreadFrame>
    </CommentCardView>
  )
}

function ExampleReplyRow({
  reply,
  showConnector,
}: {
  reply: ExampleReply
  showConnector: boolean
}): JSX.Element {
  const headerId = `example-reply-header-${reply.id}`
  return (
    <EngagementItem
      as="aside"
      aria-label="Author's reply"
      aria-labelledby={headerId}
      data-reply-id={reply.id}
      variant="lastReply"
      avatar={
        <EngagementAvatar
          hasParent={true}
          avatar=""
          username={EXAMPLE_REPLY_ACCOUNT_NAME}
          showConnector={showConnector}
        />
      }
    >
      <EngagementHeader
        id={headerId}
        username={
          <Text weight="bold" translate="no">
            {EXAMPLE_REPLY_ACCOUNT_NAME}
          </Text>
        }
        timestamp={reply.publishedAt}
      />
      <ExpandableEngagementText>
        <Text translate="no">{reply.text}</Text>
      </ExpandableEngagementText>
    </EngagementItem>
  )
}

function ExampleReplyForm({
  comment,
  onSubmit,
}: {
  comment: ExampleComment
  onSubmit: (text: string) => void
}): JSX.Element {
  const navigate = useNavigate()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const [replyText, setReplyText] = useState('')
  const insertEmoji = useReplyEmojiInsert({
    textareaRef,
    replyText,
    setReplyText,
    maxChars: comment.replyCharacterLimit,
    channelService: comment.service,
  })
  const formId = `example-reply-${comment.id}`

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const textarea = textareaRef.current
      if (!textarea) return
      textarea.focus()
      const length = textarea.value.length
      textarea.setSelectionRange(length, length)
    })
    return (): void => cancelAnimationFrame(frame)
  }, [])
  const ariaLabel = `Reply to comment from ${formatMentionUsername(comment.authorName)}`

  return (
    <EngagementReplyFormView
      id={formId}
      channelAvatar=""
      channelName={EXAMPLE_REPLY_ACCOUNT_NAME}
      ariaLabel={ariaLabel}
      suggestions={
        <EngagementReplySuggestions
          replyingToLabel={<ReplyingToLabel username={comment.authorName} />}
          suggestions={comment.suggestions.map((text, index) => ({
            id: `${comment.id}-suggestion-${index}`,
            text,
          }))}
          mostSimilarSavedReply={null}
          onSuggestionClick={(text): boolean => {
            logExampleAction('use suggestion', comment.id, text)
            setReplyText(text)
            textareaRef.current?.focus()
            return true
          }}
          onSavedReplyClick={(): boolean => false}
        />
      }
      onSubmit={(event): void => {
        event.preventDefault()
        const text = replyText.trim()
        if (!text) return
        onSubmit(text)
      }}
    >
      <ReplyForm.Field
        id={formId}
        textareaRef={textareaRef}
        replyText={replyText}
        setReplyText={setReplyText}
        ariaLabel={ariaLabel}
        maxLength={comment.replyCharacterLimit}
        service={comment.service}
      />
      <ReplyForm.Footer>
        <ReplyForm.FooterStart>
          <EmojiPickerButton
            emojiPickerRef={emojiPickerRef}
            onEmojiSelect={(emoji): void => {
              logExampleAction('insert emoji', comment.id, emoji)
              insertEmoji(emoji)
            }}
          />
          <SavedRepliesEmptyPopover
            onOpenChange={(open): void => {
              if (open) logExampleAction('open saved replies', comment.id)
            }}
          />
          <StartPostButton
            onClick={(): void => {
              logExampleAction('start a post', comment.id)
              const network = getGuestNetworkIdForService(comment.service)
              navigate({
                pathname: `${PUBLIC_PUBLISH_ROUTE_PREFIX}${newPost.route}`,
                search: network
                  ? new URLSearchParams({ network }).toString()
                  : '',
              })
            }}
          />
        </ReplyForm.FooterStart>
        <ReplyForm.FooterEnd>
          <ReplyForm.CharCount
            count={getCharsCountForService(replyText, comment.service)}
            limit={comment.replyCharacterLimit}
          />
          <ReplyForm.Submit
            label="Reply"
            disabled={!replyText.trim()}
            tooltip={<SubmitReplyShortcutHint />}
          />
        </ReplyForm.FooterEnd>
      </ReplyForm.Footer>
    </EngagementReplyFormView>
  )
}
